import { Flex, Modal, Table, Typography } from "antd";
import { type MouseEvent, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import type { User } from "~types/user";
import { AddUserButton, UsersFilters } from "./components";
import type { UserStatuses } from "./enums/userStatuses";
import { useAddUser, useDeleteUser, useFetchUserList, useGetUsersTableColumns } from "./hooks";
import type { UserTransformed } from "./types";
import { filterUsers } from "./utilities";

const PermissionUserListPage = () => {
  const [searchText, setSearchText] = useState<string>("");
  const [roles, setRoles] = useState<Set<UserRoles>>(() => new Set());
  const [status, setStatus] = useState<UserStatuses | undefined>();
  const { formatMessage } = useIntl();
  const [modal, modalContextHolder] = Modal.useModal();
  const navigate = useNavigate();
  const handleUserAddedSuccessfully = (userData: User) => setSearchText(userData.email);

  const { data, isLoading } = useFetchUserList();
  const { mutateAsync: addUser, isPending: isAddUserInProgress } = useAddUser(handleUserAddedSuccessfully);
  const { mutateAsync: deleteUser } = useDeleteUser();

  const onDeleteClick = (event: MouseEvent, userId: number) => {
    event.preventDefault();
    event.stopPropagation();

    modal.confirm({
      title: formatMessage({ id: "permissions.user.actions.delete.title" }),
      content: formatMessage({ id: "permissions.user.actions.delete.content" }),
      okText: formatMessage({ id: "delete" }),
      onOk: async () => deleteUser(userId),
      centered: true,
    });
  };

  const columns = useGetUsersTableColumns(onDeleteClick);

  const filteredData = useMemo(() => filterUsers(data || [], searchText, roles, status), [data, searchText, roles, status]);

  const openUserDetails = (userId: number) => navigate(`manage/${userId}`);

  return (
    <>
      {modalContextHolder}
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center">
          <Typography.Title level={4}>{formatMessage({ id: "permissions" })}</Typography.Title>
          <PermissionCheck permissionKeys={[PermissionNames.PERMISSIONS]} permissionOptions={[PermissionOperations.CREATE]}>
            <AddUserButton onAdd={addUser} isAddUserInProgress={isAddUserInProgress} />
          </PermissionCheck>
        </Flex>
        <Flex justify="space-between" align="center">
          <UsersFilters setRoles={setRoles} setStatus={setStatus} disabled={isLoading} />
          <SearchFilter
            searchValue={searchText}
            doSearch={setSearchText}
            placeholderIntlKey="search.two.letters"
            style={{ minWidth: 250 }}
          />
        </Flex>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          onRow={(user: UserTransformed) => {
            return {
              onClick: () => openUserDetails(user.userId),
            };
          }}
          pagination={{
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
            defaultPageSize: 50,
            showSizeChanger: true,
            position: ["bottomCenter"],
          }}
          rowClassName="is-clickable"
          size="small"
          rowKey="userId"
        />
      </Flex>
    </>
  );
};

export default PermissionUserListPage;
